import { useState, useMemo } from "react";
import { useAuth } from "wasp/client/auth";
import {
  getAllTradesForAnalytics,
  getAccountsByUser,
  getAccountGroupsByUser,
  getTradingStrategiesByUser,
  getRoutinesByUser,
  getGoalsByUser,
  getTradingSessionsByUser,
  getAccountChargesByUser,
  getTptWalletContext,
  useQuery,
} from "wasp/client/operations";
import {
  calculateMetrics,
  netPnl,
  calculateSymbolStats,
  calculateMonthlyStats,
  calculateCumulativePnl,
  calculatePnlDistribution,
  calculateRollingWinRate,
  calculateHourDayHeatmap,
  calculateTimeSlotStats,
  calculateHoldDurationData,
  calculateTagStats,
  calculateBehavioralBiases,
  calculateSessionStats,
  calculatePipStats,
  calculatePipBuckets,
  calculatePipsBySymbol,
  calculateCalmarRatio,
  calculateRecoveryFactor,
  calculateSizingData,
  calculateLongShortStats,
  calculateDayOfWeekStats,
  calculateStreakData,
  calculateConsistencyScore,
  calculateNetDailyPnl,
  calculateDrawdownCurve,
} from "../trading-journal/analytics";
import { useIsDark, useThemeId } from "../client/components/ui/card-section-header";
import { PaymentPlanId } from "../payment/plans";
import useTranslation from "../client/hooks/useTranslation";
import type { AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import type { DashboardData } from "../dashboard/widgets/DashboardDataContext";
import type { DateRange } from "../client/utils/dateRange";
import { presetToDateRange } from "../client/utils/dateRange";
import { startOfMonth } from "date-fns";
import { CURRENCY } from "../shared/currency";
import { buildAccountCalendarEvents } from "../prop-firm-manager/accountCalendarEvents";
import { chargeCalendarInstances } from "../prop-firm-manager/chargeSchedule";
import type { CalendarEvent } from "../prop-firm-manager/PropFirmCalendar";

export function useCustomPageData(opts: {
  dateRange: DateRange;
  accountFilter: AccountFilterValue;
  strategyIds: string[];
  pfmTemplateFilter: string | null;
  setPfmTemplateFilter: (id: string | null) => void;
  accountStatusFilter: string | null;
  setAccountStatusFilter: (status: string | null) => void;
}): DashboardData {
  const t = useTranslation();
  const isDark = useIsDark();
  const themeId = useThemeId();
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [chartMode, setChartMode] = useState<"pnl" | "networth">("pnl");

  const { data: authUser } = useAuth();
  const { data: chargesData, isLoading: isLoadingPfm } = useQuery(getAccountChargesByUser);
  const { data: tptWalletContext } = useQuery(getTptWalletContext);
  const charges = (chargesData ?? []) as any[];

  const [dateRange, setDateRange] = useState<DateRange>(() => presetToDateRange("last_30_days"));
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddPayoutModal, setShowAddPayoutModal] = useState(false);

  const includeInactive = opts.accountFilter.type === "all-inactive";
  const { data: allTradesRaw = [] } = useQuery(getAllTradesForAnalytics, { excludeInactive: !includeInactive });
  const { data: accountsData } = useQuery(getAccountsByUser);
  const { data: groups = [] } = useQuery(getAccountGroupsByUser);
  const { data: strategies = [] } = useQuery(getTradingStrategiesByUser);
  const { data: routines = [] } = useQuery(getRoutinesByUser);
  const { data: goals = [] } = useQuery(getGoalsByUser);
  const { data: sessions = [] } = useQuery(getTradingSessionsByUser);

  const accounts = (accountsData as any)?.accounts ?? [];
  const allTrades: any[] = allTradesRaw as any[];

  // Compute filtered account IDs (intersection of accountFilter + pfmTemplateFilter)
  const filteredAccountIds = useMemo<Set<string> | null>(() => {
    // Resolve all template IDs sharing the same name (handles system vs custom duplicates)
    const pfmTemplateIds: Set<string> | null = (() => {
      if (!opts.pfmTemplateFilter) return null;
      const selectedName = accounts.find((a: any) => a.propFirmTemplateId === opts.pfmTemplateFilter)?.propFirmTemplate?.name;
      if (!selectedName) return new Set([opts.pfmTemplateFilter]);
      const ids = accounts
        .filter((a: any) => a.propFirmTemplate?.name === selectedName)
        .map((a: any) => a.propFirmTemplateId as string);
      return new Set(ids);
    })();

    const pool: any[] = pfmTemplateIds
      ? accounts.filter((a: any) => pfmTemplateIds.has(a.propFirmTemplateId))
      : accounts;

    // Apply accountStatusFilter
    let statusPool = opts.accountStatusFilter
      ? pool.filter((a: any) => a.status === opts.accountStatusFilter)
      : pool;

    if (opts.accountFilter.type === "none") return new Set();
    if (opts.accountFilter.type === "account") {
      const found = statusPool.find((a: any) => a.id === opts.accountFilter.id);
      return found ? new Set([found.id]) : new Set();
    }
    if (opts.accountFilter.type === "group") {
      const groupAccountIds = (groups as any[]).find((g: any) => g.id === opts.accountFilter.id)
        ?.accounts?.map((a: any) => a.id) ?? [];
      const ids = statusPool.filter((a: any) => groupAccountIds.includes(a.id)).map((a: any) => a.id);
      return new Set(ids);
    }
    if (opts.accountFilter.type === "all-inactive") {
      return pfmTemplateIds || opts.accountStatusFilter ? new Set(statusPool.map((a: any) => a.id)) : null;
    }
    // "all": only active accounts
    return new Set(statusPool.filter((a: any) => a.isActive !== false).map((a: any) => a.id));
  }, [accounts, groups, opts.accountFilter, opts.pfmTemplateFilter, opts.accountStatusFilter]);

  const filteredAccounts = useMemo(() => {
    if (filteredAccountIds !== null) {
      return accounts.filter((a: any) => filteredAccountIds.has(a.id));
    }
    return accounts;
  }, [accounts, filteredAccountIds]);

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];
    const now = new Date();
    for (const acc of filteredAccounts) {
      events.push(...buildAccountCalendarEvents(acc, CURRENCY));
    }
    const chargeList = (charges ?? []) as {
      id: string;
      amount: number;
      frequency: string;
      accountId?: string | null;
      name: string;
      createdAt: Date | string;
      startDate?: Date | string | null;
      endDate?: Date | string | null;
      billingDay?: number | null;
    }[];
    for (const charge of chargeList) {
      if (charge.accountId) {
        const belongs = filteredAccounts.some((a: any) => a.id === charge.accountId);
        if (!belongs) continue;
      }
      // Même règle que les KPIs (chargeSchedule) : un abonnement fermé ne
      // projette plus aucun prélèvement après sa date de fin.
      const eventType = charge.frequency === "one_time" ? "expense" : "recurring";
      for (const { date, amount } of chargeCalendarInstances(charge, now)) {
        events.push({ date, type: eventType, amount, currency: CURRENCY, label: charge.name });
      }
    }
    return events;
  }, [filteredAccounts, charges]);

  const filteredTrades = useMemo(() => {
    if (filteredAccountIds !== null) {
      return allTrades.filter((t: any) => filteredAccountIds.has(t.account?.id ?? t.accountId));
    }
    return allTrades;
  }, [allTrades, filteredAccountIds]);

  const statsTradesList = useMemo(() => {
    let filtered = filteredTrades;
    if (opts.dateRange?.from) {
      const from = new Date(opts.dateRange.from);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter((t: any) => new Date(t.date) >= from);
    }
    if (opts.dateRange?.to) {
      const to = new Date(opts.dateRange.to);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter((t: any) => new Date(t.date) <= to);
    }
    if (opts.strategyIds.length > 0) {
      filtered = filtered.filter((t: any) =>
        t.strategies?.some((s: any) => opts.strategyIds.includes(s.id))
      );
    }
    return filtered;
  }, [filteredTrades, opts.dateRange, opts.strategyIds]);

  const metrics = useMemo(() => calculateMetrics(statsTradesList), [statsTradesList]);

  const analyticsData = useMemo(() => ({
    symbolStats: calculateSymbolStats(statsTradesList),
    monthlyStats: calculateMonthlyStats(statsTradesList),
    cumulativePnl: calculateCumulativePnl(statsTradesList).map((p) => ({ x: new Date(p.date).getTime(), y: p.cumulativePnl })),
    pnlDistribution: calculatePnlDistribution(statsTradesList),
    rollingWinRate: calculateRollingWinRate(statsTradesList),
    heatmapData: calculateHourDayHeatmap(statsTradesList),
    timeSlotStats: calculateTimeSlotStats(statsTradesList),
    holdDurationData: calculateHoldDurationData(statsTradesList),
    tagStats: calculateTagStats(statsTradesList),
    behavioralBiases: calculateBehavioralBiases(statsTradesList),
    sessionStats: calculateSessionStats(sessions as any[], statsTradesList),
    pipStats: calculatePipStats(statsTradesList),
    pipBuckets: calculatePipBuckets(statsTradesList),
    pipBySymbol: calculatePipsBySymbol(statsTradesList),
    calmarRatio: calculateCalmarRatio(statsTradesList),
    recoveryFactor: calculateRecoveryFactor(statsTradesList),
    sizingData: calculateSizingData(statsTradesList),
    longShort: calculateLongShortStats(statsTradesList),
    dayOfWeek: calculateDayOfWeekStats(statsTradesList),
    streakData: calculateStreakData(statsTradesList),
    consistencyScore: (() => {
      const sd = calculateStreakData(statsTradesList);
      return calculateConsistencyScore(statsTradesList, sd);
    })(),
    netDailyPnl: calculateNetDailyPnl(statsTradesList),
    drawdownCurve: calculateDrawdownCurve(statsTradesList),
  }), [statsTradesList, sessions]);

  const equityCurveData = useMemo(() => {
    const closed = [...statsTradesList]
      .filter((t) => t.pnl != null)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (closed.length === 0) return { points: [], totalPnl: 0, xMin: 0, xMax: 0 };
    let running = 0;
    const points: { x: number; y: number }[] = [];
    for (const t of closed) {
      running += netPnl(t);
      points.push({ x: new Date(t.date).getTime(), y: parseFloat(running.toFixed(2)) });
    }
    return {
      points,
      totalPnl: running,
      xMin: points[0]?.x ?? 0,
      xMax: points[points.length - 1]?.x ?? 0,
    };
  }, [filteredTrades]);

  return {
    trades: statsTradesList,
    routines: routines as any[],
    goals: goals as any[],
    accounts: filteredAccounts,
    allAccounts: accounts,
    groups: groups as any[],
    strategies: strategies as any[],
    sessions: sessions as any[],
    metrics,
    statsTrades: statsTradesList,
    statsTradesList,
    equityCurveData,
    analyticsData,
    accountEvents: [],
    fundedEvents: [],
    payoutEvents: [],
    violationsByDate: new Map(),
    pendingRoutines: (routines as any[]).filter((r: any) => r.isActive),
    topGoals: (() => {
      const today = new Date();
      return (goals as any[])
        .filter((g: any) => !g.isCompleted && g.targetValue > 0 && g.type !== "custom")
        .map((g: any) => {
          const progress = Math.min((g.currentValue / g.targetValue) * 100, 100);
          const daysLeft = g.deadline
            ? Math.ceil((new Date(g.deadline).getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
            : null;
          return { goal: g, progress, daysLeft };
        })
        .slice(0, 3);
    })(),
    accountFilter: opts.accountFilter,
    setAccountFilter: () => {},
    strategyIds: opts.strategyIds,
    setStrategyIds: () => {},
    statsPeriod: "30d" as const,
    statsStartDate: null,
    statsEndDate: null,
    setSelectedDay,
    handleCompleteRoutine: () => {},
    setSelectedMonth,
    onAddTrade: () => {},
    onPromoteToFunded: () => {},
    onOpenPlanModal: () => {},
    isDark,
    themeId,
    isFreePlan: !authUser || authUser.subscriptionPlan === PaymentPlanId.Free || !authUser.subscriptionPlan,
    readOnly: true,
    hasMentor: false,
    t,
    chartMode,
    setChartMode,
    charges,
    tptWalletContext,
    filteredAccounts,
    calendarEvents,
    pfmTemplateFilter: opts.pfmTemplateFilter,
    setPfmTemplateFilter: opts.setPfmTemplateFilter,
    accountStatusFilter: opts.accountStatusFilter,
    setAccountStatusFilter: opts.setAccountStatusFilter,
    dateRange,
    setDateRange,
    calendarMonth,
    setCalendarMonth,
    showImportModal,
    setShowImportModal,
    showAddPayoutModal,
    setShowAddPayoutModal,
    isLoadingPfm: isLoadingPfm ?? false,
    userId: authUser?.id,
    hiddenAccountsCount: 0,
    showPlanModal: false,
    setShowPlanModal: () => {},
  };
}
